import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useSpamSchutz, Honigtopf } from "../components/SpamSchutz";
import { Mail, Send, CheckCircle } from "lucide-react";

const Contact = () => {
  const { t } = useLanguage();

  const seoData = {
    title: "Kontakt - BROVKO KI-Beratung | Jetzt Gespräch vereinbaren",
    description: "Kontaktieren Sie BROVKO für Ihre KI-Transformation. Wir beraten Sie zu AI-Strategien, Custom GPT-Lösungen und nachhaltige KI-Integration.",
    keywords: "BROVKO Kontakt, KI-Beratung Kontakt, AI Transformation Anfrage",
    canonicalUrl: "/contact",
    ogTitle: "Kontakt - BROVKO KI-Beratung",
    ogDescription: "Kontaktieren Sie uns für Ihre KI-Transformation. Kostenlose Erstberatung."
  };


  const { honigtopf, spamFelder } = useSpamSchutz();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      toast({
        title: "Bitte füllen Sie alle Pflichtfelder aus",
        description: "Vorname, Nachname, E-Mail und Nachricht sind erforderlich.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(await spamFelder()),
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || '',
          company: formData.company || '',
          subject: formData.subject || 'Kontaktanfrage',
          message: formData.message,
          pageSource: 'Kontaktseite',
          currentUrl: window.location.href,
          timestamp: new Date().toISOString()
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Nachricht erfolgreich gesendet!",
          description: "Vielen Dank für Ihre Anfrage. Wir melden uns zeitnah bei Ihnen.",
        });

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: ""
        });
      } else {
        throw new Error(result.message || 'Fehler beim Senden');
      }
    } catch (error) {
      toast({
        title: "Fehler beim Senden",
        description: "Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface-sunken">
      <SEOHead {...seoData} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-40 pb-10 bg-gradient-to-br from-slate-900 to-teal-900">
        <div className="container mx-auto px-4">
          <div className=" text-white">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] mb-4">
              Kontakt
            </h1>
            <p className="text-base md:text-lg text-ink-muted max-w-[62ch]">
              Bereit für Ihre KI-Transformation? Lassen Sie uns über Ihre Herausforderungen sprechen.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            
            {/* Contact Form */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
                  Nachricht senden
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <Honigtopf {...honigtopf} />
                  {/* Name Fields */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        Vorname *
                      </label>
                      <Input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="Ihr Vorname"
                        className="w-full"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        Nachname *
                      </label>
                      <Input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Ihr Nachname"
                        className="w-full"
                        required
                      />
                    </div>
                  </div>

                  {/* Email and Phone */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        E-Mail-Adresse *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="ihre.email@unternehmen.de"
                        className="w-full"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        Telefonnummer
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+49 (0) 123 456 789"
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Company and Subject */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        Unternehmen
                      </label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Ihr Unternehmen"
                        className="w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        Betreff
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Betreff Ihrer Anfrage"
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-2">
                      Ihre Nachricht *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Ihre Nachricht..."
                      className="w-full min-h-[120px]"
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-brand hover:bg-brand-600 text-ink-onBrand py-3 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-hairline-strong"></div>
                        Nachricht wird gesendet...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Send className="h-5 w-5" />
                        Nachricht senden
                      </div>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-6">
                  Kontaktinformationen
                </h2>
                <p className="text-ink-muted mb-8">
                  Haben Sie Fragen zu unseren KI-Lösungen oder möchten Sie ein unverbindliches Gespräch führen? 
                  Kontaktieren Sie uns gerne über eines der folgenden Kanäle.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <Card className="border-l-4 border-l-teal-500 border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="bg-brand/15 p-3 rounded-full">
                        <Mail className="h-6 w-6 text-brand-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-ink">E-Mail</h3>
                        <p className="text-ink-muted">email@brovko.de</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Response Time */}
              <Card className="bg-gradient-to-r from-brand/10 to-blue-500/10 border-0">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="h-6 w-6 text-brand-400" />
                    <h3 className="font-semibold text-ink">Schnelle Antwort</h3>
                  </div>
                  <p className="text-ink-muted">
                    Wir antworten normalerweise innerhalb von 24 Stunden auf Ihre Anfrage.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;